import { useEffect, useState } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type ThemeStore = {
  theme: ThemePreference;
  setTheme: (theme: ThemePreference) => void;
  toggleTheme: () => void;
};

export type ThemePreference = "system" | "light" | "dark";

/**
 * The preference and the rendered theme are not always the same thing: "system"
 * resolves through the device media query. Quick switches use the rendered value
 * so their checked state is truthful and then persist an explicit light/dark choice.
 */
export function useResolvedTheme(): Exclude<ThemePreference, "system"> {
  const preference = useThemeStore((state) => state.theme);
  const [systemDark, setSystemDark] = useState(false);

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const sync = () => setSystemDark(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  if (preference === "system") return systemDark ? "dark" : "light";
  return preference;
}

export const useThemeStore = create<ThemeStore>()(
  persist(
    (set) => ({
      theme: "system",
      setTheme: (theme) => set({ theme }),
      toggleTheme: () =>
        set((state) => ({
          theme: state.theme === "dark" ? "light" : "dark",
        })),
    }),
    {
      name: "theme-storage",
    },
  ),
);
